import { motion } from "framer-motion";
import { PageHero, CTASection } from "@/components/Bits";
import { FloatingQuote } from "@/components/FloatingQuote";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { YouTubeEmbed } from "@/components/YouTubeEmbed";
import { IMG } from "@/data/content";

const CHAPTERS = [
  {
    n: "01",
    title: "Global economies are driven by research and innovation",
    body: [
      "Today's global economies are driven by research, innovation, and patented technologies emerging from advanced scientific laboratories and leading universities. From everyday household products to highly complex industrial systems, nearly every sector depends on continuous breakthroughs in Science and Technology.",
      "Nations that lead in research shape the future of the world, and this leadership begins with how students are educated at the school level.",
    ],
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/phbms3b0_Global%20economy.png",
  },
  {
    n: "02",
    title: "Schools as centres of problem-solving and scientific thinking",
    body: [
      "Schools must therefore evolve into centers of problem-solving and scientific thinking, where students are trained from the early years to question, investigate, analyze, and apply knowledge.",
      "When curiosity becomes a daily discipline, classrooms stop being places of passive content delivery and become laboratories of discovery.",
    ],
    img: IMG.lab3,
  },
  {
    n: "03",
    title: "True problem-solving is built on deep concept mastery",
    body: [
      "Advanced problem solving is impossible without deep conceptual mastery. Every capable innovator first becomes fluent in the fundamentals — verifying each concept through experimentation before applying it to open-ended, real-world challenges.",
      "This is the foundation on which THECREATOR Education builds future scientists, researchers, and entrepreneurs.",
    ],
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/xkx7aj5f_Ikhbaat%20PS.png",
  },
  {
    n: "04",
    title: "Why chalk-and-talk fails to build concepts",
    body: [
      "For generations, education has depended on chalk-and-talk. The teacher explains an idea, writes on the board, and students are expected to listen, remember and reproduce it in an examination. Completing the syllabus, however, is not the same as completing the learning.",
      "A concept explained is not necessarily a concept understood, and a concept remembered for an exam is rarely a concept that can be applied. At best, chalk-and-talk creates familiarity — the student knows a definition or a formula. Familiarity, however, is not mastery.",
    ],
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/mshg4mb2_Screenshot%202026-01-31%20155637.png",
  },
  {
    n: "05",
    title: "Why memorization is not learning",
    body: [
      "Science and Mathematics are not subjects of memorization. They are disciplines through which humanity creates solutions to problems and develops technologies, discoveries, inventions, patents, research papers, and Nobel Prize-winning innovations that improve human life and society.",
      "The real purpose of learning Physics, Chemistry, Biology, and Mathematics is not merely to pass examinations, but to develop the ability to think, investigate, innovate, and solve real-world problems.",
    ],
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/qsmezz9f_Bio%20Lab.png",
  },
  {
    n: "06",
    title: "Science is learned the way swimming is — by doing it.",
    body: [
      "Science cannot be mastered by listening to a lecture, no matter how well the lecture is delivered. A concept becomes real only when a student sees it demonstrated, then performs the experiment themselves and verifies it with their own eyes and hands.",
      "In every PBL-TDLE class the teacher first demonstrates the concept with real, global-standard apparatus. Every student then follows through the same six steps — observing, measuring, recording, analysing and concluding — until learning becomes visible, correctable and verifiable in real time.",
    ],
    cycle: ["Observe", "Investigate", "Measure", "Analyse", "Verify", "Apply"],
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/hcg2ies3_WhatsApp%20Image%202022-08-04%20at%208.10.38%20AM.jpeg",
  },
];

export default function Vision() {
  return (
    <div data-testid="vision-page">
      <FloatingQuote />
      <PageHero
        n="01"
        label="The Vision & Philosophy"
        lines={["THECREATOR Education :", "The Vision & Philosophy"]}
        intro="Science and Mathematics are not subjects of memorization. They are disciplines through which humanity creates solutions to problems."
      />

      {/* Split editorial pull */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5 relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black" data-testid="vision-hero-video">
            <YouTubeEmbed id="UT3xHaBIVYs" title="THECREATOR Education — Vision & Philosophy" />
          </div>
          <Reveal className="md:col-span-7 md:pl-10">
            <p className="font-display text-2xl md:text-4xl text-white/85 leading-[1.2] tracking-tight font-light">
              “The real purpose of learning Physics, Chemistry, Biology, and Mathematics is not merely to pass examinations, but to develop the ability to think, investigate, innovate, and solve real-world problems.”
            </p>
          </Reveal>
        </div>
      </section>

      {CHAPTERS.map((c, i) => (
        <section key={c.n} className={`border-t border-white/10 ${i % 2 ? "bg-[#070d18]" : ""}`} data-testid={`vision-chapter-${c.n}`}>
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <div className="md:sticky md:top-28">
                <SectionLabel n={c.n}>Chapter</SectionLabel>
                <h2 className="font-display text-white mt-6 tracking-tight leading-tight font-light text-3xl md:text-4xl">{c.title}</h2>
              </div>
            </div>
            <div className="md:col-span-8 md:col-start-5">
              {c.body.map((p, j) => (
                <Reveal key={j} delay={j * 0.08}>
                  <p className="text-white/65 leading-relaxed mb-6 max-w-2xl text-lg md:text-xl">{p}</p>
                </Reveal>
              ))}
              {c.cycle && (
                <Reveal delay={0.16}>
                  <div className="mt-2 mb-2 flex flex-wrap items-center gap-3 max-w-2xl" data-testid={`vision-cycle-${c.n}`}>
                    {c.cycle.map((step, k) => {
                      const cols = ["text-c-blue", "text-c-teal", "text-c-amber", "text-c-violet", "text-c-rose", "text-c-green"];
                      return (
                        <div key={step} className="flex items-center gap-3">
                          <span className={`inline-flex items-center rounded-full border border-white/15 px-4 py-2 font-mono-label text-[11px] tracking-label uppercase ${cols[k % cols.length]}`}>
                            {step}
                          </span>
                          {k < c.cycle.length - 1 && <span className="text-accent">→</span>}
                        </div>
                      );
                    })}
                  </div>
                </Reveal>
              )}
              <Reveal delay={0.1}>
                <div className="mt-8 aspect-video overflow-hidden border border-white/10 spotlight-clip max-w-2xl">
                  <motion.img
                    src={c.img}
                    alt={c.title}
                    className="w-full h-full object-cover"
                    initial={{ scale: 1.1 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      <CTASection />
    </div>
  );
}
